// ============================================
// components/gunny/GunnyDeliveriesModal.tsx
// Records and lists PO→godown deliveries so per-godown on-hand stock becomes
// computable. Opens from the Gunny POs list on approved/fulfilled POs.
// ============================================
import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, Plus, Trash2, Truck } from 'lucide-react';
import { gunnyPosAPI, godownsAPI } from '../../api/services';
import type { GunnyPoDelivery } from '../../api/services';
import { bagTypeLabel } from './bagTypes';

interface PO {
  id: number;
  season_id: number;
  po_number: string;
  total_bags: number;
  delivery_district_id: number;
}

interface Props {
  po: PO | null;
  onClose: () => void;
  onChanged?: () => void;
}

interface GodownOpt {
  id: number;
  godown_name: string;
  godown_type: string | null;
  capacity_mts: number | null;
  is_active: boolean;
}

export const GunnyDeliveriesModal: React.FC<Props> = ({ po, onClose, onChanged }) => {
  const [deliveries, setDeliveries] = useState<GunnyPoDelivery[]>([]);
  const [godowns, setGodowns] = useState<GodownOpt[]>([]);
  const [fulfillment, setFulfillment] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [godownId, setGodownId] = useState<number>(0);
  const [bags, setBags] = useState<number>(0);
  const [date, setDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState<string>('');

  const open = !!po;

  const load = async () => {
    if (!po) return;
    setLoading(true);
    setErr(null);
    try {
      const [dRes, gRes, fRes] = await Promise.all([
        gunnyPosAPI.listDeliveries(po.season_id, po.id),
        godownsAPI.list(po.delivery_district_id),
        gunnyPosAPI.fulfillment(po.season_id, po.id).catch(() => ({ data: null })),
      ]);
      setDeliveries(Array.isArray(dRes.data) ? dRes.data : []);
      setGodowns((gRes.data as GodownOpt[]).filter((g) => g.is_active));
      setFulfillment(fRes.data || null);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, po?.id]);

  const delivered = useMemo(
    () => deliveries.reduce((s, d) => s + Number(d.bags), 0),
    [deliveries],
  );
  const remaining = po ? Math.max(po.total_bags - delivered, 0) : 0;

  if (!open || !po) return null;

  const submit = async () => {
    if (!godownId || bags <= 0 || bags > remaining || !date) return;
    setSaving(true);
    setErr(null);
    try {
      await gunnyPosAPI.recordDelivery(po.season_id, po.id, {
        godown_id: godownId,
        bags,
        delivered_on: date,
        remarks: remarks.trim() || undefined,
      });
      setGodownId(0); setBags(0); setRemarks('');
      await load();
      if (onChanged) onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to record delivery');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Remove this delivery record?\nThis only works if no bags from it have been allotted yet.')) return;
    try {
      await gunnyPosAPI.removeDelivery(po.season_id, po.id, id);
      await load();
      if (onChanged) onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to remove delivery');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex items-center gap-2 sticky top-0 bg-white">
          <Truck className="w-5 h-5 text-amber-600" />
          <div>
            <h2 className="font-semibold text-gray-900">Deliveries for PO {po.po_number}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Record vendor deliveries against specific godowns. Per-godown on-hand stock comes from these records.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 bg-amber-50 border-b grid grid-cols-3 gap-3 text-sm">
          <div>
            <p className="text-xs text-gray-500">PO total</p>
            <p className="font-semibold tabular-nums">{po.total_bags.toLocaleString()} bags</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Delivered so far</p>
            <p className="font-semibold tabular-nums">{delivered.toLocaleString()} bags</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Remaining to deliver</p>
            <p className={`font-semibold tabular-nums ${remaining === 0 ? 'text-emerald-700' : 'text-amber-800'}`}>
              {remaining.toLocaleString()} bags
            </p>
          </div>
        </div>

        {err && (
          <div className="mx-6 mt-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" /> {err}
          </div>
        )}

        {/* Per-line-item fulfillment (one row per (PACS × bag-type) — same PACS
            can appear twice with different bag types, each is its own line). */}
        {fulfillment?.per_pacs?.length > 0 && (
          <div className="px-6 py-4 border-b">
            <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Per-line-item allocation</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-600 bg-gray-50">
                  <tr className="text-left">
                    <th className="px-2 py-1">PACS</th>
                    <th className="px-2 py-1">Commodity</th>
                    <th className="px-2 py-1">Bag</th>
                    <th className="px-2 py-1 text-right">Rate</th>
                    <th className="px-2 py-1 text-right">Ordered</th>
                    <th className="px-2 py-1 text-right">Dispatched</th>
                    <th className="px-2 py-1 text-right">Remaining</th>
                    <th className="px-2 py-1 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {fulfillment.per_pacs.map((p: any) => {
                    const ordered = Number(p.ordered || 0);
                    const delivered = Number(p.received || 0) + Number(p.pending_ack || 0);
                    const remaining = Number(p.remaining || 0);
                    const rate = Number(p.rate || 0);
                    const fillPct = ordered > 0 ? Math.round((delivered / ordered) * 100) : 0;
                    return (
                      <tr key={p.po_item_id ?? `${p.pacs_id}-${p.bag_type}`} className="border-t">
                        <td className="px-2 py-1">{p.pacs_name || `pacs#${p.pacs_id}`}</td>
                        <td className="px-2 py-1 text-xs">
                          {p.commodity_name ? (
                            <span className="inline-block px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px]">{p.commodity_name}</span>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-2 py-1 text-xs text-gray-700">
                          {bagTypeLabel(p.bag_type)}{p.specification ? ` · ${p.specification}` : ''}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums text-gray-600">
                          {rate > 0 ? `₹ ${rate.toFixed(2)}` : '—'}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{ordered.toLocaleString()}</td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {delivered.toLocaleString()}
                          <span className="text-xs text-gray-500 ml-1">({fillPct}%)</span>
                        </td>
                        <td className={`px-2 py-1 text-right tabular-nums font-medium ${remaining === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {remaining.toLocaleString()}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums text-gray-700">
                          {rate > 0 ? `₹ ${(rate * ordered).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-t bg-gray-50 font-semibold">
                    <td className="px-2 py-1" colSpan={4}>Total</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fulfillment.ordered_total?.toLocaleString()}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fulfillment.delivered_total?.toLocaleString()}</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${fulfillment.remaining_total === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {fulfillment.remaining_total?.toLocaleString()}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      ₹ {(fulfillment.per_pacs.reduce((s: number, r: any) => s + Number(r.rate || 0) * Number(r.ordered || 0), 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Existing */}
        <div className="px-6 py-4 border-b">
          <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Existing deliveries</p>
          {loading ? (
            <p className="text-xs text-gray-500">Loading…</p>
          ) : deliveries.length === 0 ? (
            <p className="text-xs text-gray-500 italic">No deliveries recorded yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-600 bg-gray-50">
                <tr className="text-left">
                  <th className="px-2 py-1">Date</th>
                  <th className="px-2 py-1">Destination</th>
                  <th className="px-2 py-1">Vehicle / Invoice / E-way</th>
                  <th className="px-2 py-1 text-right">Bags</th>
                  <th className="px-2 py-1">Status</th>
                  <th className="px-2 py-1 text-right" />
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d: any) => {
                  const inTransit = !d.received_at && !d.rejected_at;
                  return (
                    <tr key={d.id} className="border-t">
                      <td className="px-2 py-1">{d.dispatch_date || d.delivered_on}</td>
                      <td className="px-2 py-1">
                        {d.pacs?.name ? (
                          <span>{d.pacs.name} <span className="text-xs text-gray-500">(PACS)</span></span>
                        ) : d.godown?.godown_name ? (
                          <span>{d.godown.godown_name} <span className="text-xs text-gray-500">({(d.godown.godown_type || 'godown').toUpperCase()})</span></span>
                        ) : '—'}
                      </td>
                      <td className="px-2 py-1 text-xs text-gray-600">
                        {d.vehicle_number && <div className="font-mono">{d.vehicle_number}</div>}
                        {d.supplier_invoice_no && <div>Inv: {d.supplier_invoice_no}</div>}
                        {d.eway_bill_no && <div>EW: {d.eway_bill_no}</div>}
                        {!d.vehicle_number && !d.supplier_invoice_no && !d.eway_bill_no && (d.remarks || '—')}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">{Number(d.bags).toLocaleString()}</td>
                      <td className="px-2 py-1 text-xs">
                        {d.rejected_at ? (
                          <span title={d.rejection_reason || ''} className="text-rose-700">Rejected</span>
                        ) : d.received_at ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-emerald-700">Received {Number(d.received_bags || 0).toLocaleString()}</span>
                            {d.utilization_receipt?.verified_at ? (
                              <span title={`Verified ${new Date(d.utilization_receipt.verified_at).toLocaleString()}`}
                                className="text-[10px] text-emerald-700">✓ DM confirmed</span>
                            ) : d.utilization_receipt?.rejected_at ? (
                              <span title={d.utilization_receipt.rejection_reason || ''}
                                className="text-[10px] text-rose-700">⚠ DM rejected</span>
                            ) : (
                              <span className="text-[10px] text-amber-700">society acked — DM confirm pending</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-amber-700">In transit</span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-right whitespace-nowrap">
                        {inTransit && d.pacs_id && (
                          <button
                            onClick={async () => {
                              const v = window.prompt(`Acknowledge as DM on behalf of ${d.pacs?.name || 'PACS'}?\n\nBags actually received:`, String(d.bags));
                              if (!v) return;
                              const n = Number(v);
                              if (!(n > 0)) { setErr('Enter a positive number'); return; }
                              if (n > d.bags) { setErr(`Cannot exceed dispatched (${d.bags})`); return; }
                              try {
                                await gunnyPosAPI.acknowledgeDelivery(po!.season_id, po!.id, d.id, { received_bags: n });
                                await load();
                                if (onChanged) onChanged();
                              } catch (e: any) {
                                setErr(e?.response?.data?.message || 'Acknowledge failed');
                              }
                            }}
                            title="DM acknowledges on behalf of PACS — also confirms receipt in one click"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded mr-1">
                            Ack as DM
                          </button>
                        )}
                        <button onClick={() => remove(d.id)} className="text-red-500 hover:text-red-700" title="Remove">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* New delivery form */}
        {remaining > 0 && (
          <div className="px-6 py-4">
            <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Record a delivery</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Godown</label>
                <select value={godownId} onChange={(e) => setGodownId(Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded text-sm bg-white">
                  <option value={0}>Select godown…</option>
                  {godowns.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.godown_name}{g.godown_type ? ` (${g.godown_type.toUpperCase()})` : ''}
                      {g.capacity_mts ? ` · ${Number(g.capacity_mts).toLocaleString()} MT` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bags</label>
                <input type="number" min={0} max={remaining} value={bags || ''}
                  onChange={(e) => setBags(Number(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded text-sm"
                  placeholder={`Up to ${remaining}`} />
                {bags > remaining && <p className="text-xs text-red-600 mt-1">Exceeds remaining ({remaining.toLocaleString()}).</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivered on</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm" placeholder="Vehicle no, gate pass, etc." />
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Close</button>
          {remaining > 0 && (
            <button
              onClick={submit}
              disabled={saving || !godownId || bags <= 0 || bags > remaining}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {saving ? 'Recording…' : 'Record delivery'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GunnyDeliveriesModal;
